import { type ReactNode, useEffect } from 'react';
import { AlarmClock, Calendar, CalendarClock, CalendarOff, CalendarRange, ChevronsDown, CircleCheckBig } from 'lucide-react';
import { flattenPages } from '@missions/api-client';
import { Button } from '@missions/ui';
import { useApi, useInfinite } from '../lib/hooks';
import { setPageTitle } from '../lib/seo';
import { useT } from '../lib/session';
import { useChrome, useShell } from '../layout/shell-context';
import { GoToCard, OnThisPage, ProjectTree } from '../layout/sidebar-parts';
import { useProjects } from '../features/missions/data';
import { MissionRow } from './MissionControl';

type Bucket = 'overdue' | 'today' | 'this_week' | 'later' | 'no_date';
const BUCKETS: [Bucket, string, ReactNode][] = [
  ['overdue', 'Overdue', <AlarmClock key="o" />],
  ['today', 'Today', <CalendarClock key="t" />],
  ['this_week', 'This week', <CalendarRange key="w" />],
  ['later', 'Later', <Calendar key="l" />],
  ['no_date', 'No due date', <CalendarOff key="n" />],
];

function BucketCard({ bucket, label, icon, count }: { bucket: Bucket; label: string; icon: ReactNode; count: number | undefined }) {
  const shell = useShell();
  const t = useT();
  const { flat } = useProjects(shell.workspaceId);
  const byId = new Map(flat.map((p) => [p.id, p]));
  const q = useInfinite('myMissions.list', { query: { bucket, limit: 10 } });
  const items = flattenPages(q.data);
  return (
    <div className="card" id={`mw-${bucket}`} style={{ scrollMarginTop: 12 }}>
      <div className="card-h">
        <span className="tile" style={bucket === 'overdue' ? { background: 'var(--danger-50)', color: 'var(--danger)' } : undefined}>{icon}</span>
        <h2>{label}</h2>
        <span className="sub">{count ?? items.length} {(count ?? items.length) === 1 ? t.item : t.items}</span>
      </div>
      <div>
        {q.isPending ? (
          <div style={{ padding: 16 }}>
            <span className="sk ui-skel" style={{ width: '70%', display: 'block', height: 14 }} />
            <span className="sk ui-skel" style={{ width: '50%', display: 'block', height: 14, marginTop: 14 }} />
          </div>
        ) : !items.length ? (
          <div style={{ padding: '18px 16px', color: 'var(--text-3)' }}>{bucket === 'overdue' ? 'Nothing overdue. Nice work! 🎉' : `No ${t.items} here.`}</div>
        ) : (
          items.map((m) => <MissionRow key={m.id} m={m} projectName={byId.get(m.projectId)?.name} projectColor={byId.get(m.projectId)?.color} />)
        )}
      </div>
      {q.hasNextPage && (
        <div className="gfoot" style={{ paddingLeft: 16 }}>
          Showing {items.length} of {count ?? '…'}
          <Button variant="ghost" className="more" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            <ChevronsDown />Load more
          </Button>
        </div>
      )}
    </div>
  );
}

/** My Missions: everything assigned to me across projects, grouped Overdue / Today / This week / Later. */
export function MyMissions() {
  const t = useT();
  const counts = useApi('myMissions.counts');
  useEffect(() => setPageTitle(t.myItems), [t.myItems]);
  useChrome(
    {
      section: 'Work',
      current: <><CircleCheckBig style={{ color: 'var(--text-3)' }} /><span className="nm">{t.myItems}</span></>,
      secondaryTitle: t.myItems,
      secondary: (
        <>
          <div className="glabel" style={{ paddingTop: 4 }}>{t.myItems}</div>
          {BUCKETS.map(([k, l, i]) => (
            <button key={k} className="si" onClick={() => document.getElementById(`mw-${k}`)?.scrollIntoView({ behavior: 'smooth' })}>
              {i}
              <span className="lbl">{l}</span>
              <span className="cnt">{counts.data?.[k] ?? ''}</span>
            </button>
          ))}
          <div className="s-sep" />
          <div className="glabel" style={{ paddingTop: 2 }}>All projects</div>
          <ProjectTree />
        </>
      ),
      rail: (
        <>
          <OnThisPage links={BUCKETS.map(([k, l]) => [`mw-${k}`, l])} />
          <GoToCard />
        </>
      ),
    },
    [t, counts.data],
  );
  return (
    <div className="page" id="pgMyWork">
      <div className="inbox-h">
        <div>
          <h1>{t.myItems}</h1>
          <p>Everything assigned to you across projects, sorted by due date.</p>
        </div>
      </div>
      {BUCKETS.map(([k, l, i]) => (
        <BucketCard key={k} bucket={k} label={l} icon={i} count={counts.data?.[k]} />
      ))}
    </div>
  );
}
